import fetch from 'isomorphic-unfetch';
import { CHAT_ROUTES } from '@belong/types';
import { IInitiateChatResponse, IInitiateChatResponseV2, IQueuePositionResponse } from '@belong/types/api/chat';
import { HEADERS } from '@belong/constants';
import type { IOptionalStringMap } from '@belong/type-utils';
import { getSessionInfo } from '../lib/getSessionInfo';
import { getEnvVars } from '../lib/getEnvVars';

// Chat API Documentation:
// https://belongranda.atlassian.net/wiki/spaces/BEL/pages/2075230625/57+CST+-+Belong+Live+Chat

// Duplicate of `compile` from `@belong/url-utils`, to avoid the bundling of Next
// deps that are imported in that file.
const compile = (urlTemplate: string, values: IOptionalStringMap): string =>
  Object.entries(values).reduce((acc, [key, value]) => {
    return acc.replace(`:${key}`, (value ?? '').toString());
  }, urlTemplate);

// Simple version of what genericFetch returns.
type ParsedFetchResult<T = unknown> = Promise<{ response: Response; body: T }>;
const withParsedBody = async <T>(response: Response): ParsedFetchResult<T> => ({
  response,
  body: await response.json()
});

const makeHeaders = (): HeadersInit => {
  const { accessToken = '', correlationId = '' } = getSessionInfo();

  return {
    [HEADERS.ACCEPT]: 'application/json',
    [HEADERS.CONTENT_TYPE]: 'application/json',
    [HEADERS.ACCESS_TOKEN]: accessToken,
    [HEADERS.CONSUMER_ID]: 'digital',
    [HEADERS.CORRELATION_ID]: correlationId,
    [HEADERS.CACHE_CONTROL]: 'no-store',
    [HEADERS.PRAGMA]: 'no-cache'
  };
};

const getSessionsBody = (existingSessionKey?: string): { chatSessionKey?: string; chatOriginLocation: string } => {
  return {
    ...(existingSessionKey ? { chatSessionKey: existingSessionKey } : {}),
    chatOriginLocation: window.parent.location.pathname
  };
};

const getInitRequest = (
  accessToken: string,
  existingSessionKey?: string,
  isSessionsV2Enabled?: boolean
): { endpoint: string; body: string | undefined } => {
  let endpoint: string;
  let body: string | undefined;

  if (isSessionsV2Enabled) {
    endpoint = accessToken ? CHAT_ROUTES.AUTHENTICATED_INITIATE_V2 : CHAT_ROUTES.ANONYMOUS_INITIATE_V2;
    body = JSON.stringify(getSessionsBody(existingSessionKey));
  } else {
    endpoint = accessToken ? CHAT_ROUTES.AUTHENTICATED_INITIATE : CHAT_ROUTES.ANONYMOUS_INITIATE;
    body =
      existingSessionKey &&
      JSON.stringify({
        Attributes: {
          ChatSessionKey: existingSessionKey
        }
      });
  }

  return { endpoint, body };
};

export class ChatAPI {
  // eslint-disable-next-line class-methods-use-this
  private getApiHost(): string {
    // Chat endpoints currently have no blue envs and need to always use green
    return getEnvVars().gateway?.host || '';
  }

  initiateChat(
    existingSessionKey?: string,
    isSessionsV2Enabled?: boolean
  ): ParsedFetchResult<IInitiateChatResponse | IInitiateChatResponseV2> {
    const { accessToken } = getSessionInfo();
    const { endpoint, body } = getInitRequest(accessToken, existingSessionKey, isSessionsV2Enabled);

    const url = `${this.getApiHost()}${endpoint}`;

    return fetch(url, { headers: makeHeaders(), body, method: 'POST' }).then(response => withParsedBody(response));
  }

  sendRating(contactId: string, chatRating: number): ReturnType<typeof fetch> {
    const endpoint = compile(CHAT_ROUTES.FEEDBACK_RATING, { contactId });
    const url = `${this.getApiHost()}${endpoint}`;
    const body = JSON.stringify({ CustomerChatRating: chatRating });

    return fetch(url, { headers: makeHeaders(), body, method: 'PUT' });
  }

  sendFeedbackComment(contactId: string, comment: string): ReturnType<typeof fetch> {
    const endpoint = compile(CHAT_ROUTES.FEEDBACK_COMMENT, { contactId });
    const url = `${this.getApiHost()}${endpoint}`;
    const body = JSON.stringify({ customerChatRatingComment: comment });

    return fetch(url, { headers: makeHeaders(), body, method: 'PUT' });
  }

  getQueuePosition(contactId: string): ParsedFetchResult<IQueuePositionResponse> {
    const endpoint = compile(CHAT_ROUTES.QUEUE_POSITION, { contactId });
    const url = `${this.getApiHost()}${endpoint}`;

    return fetch(url, { headers: makeHeaders() }).then(response => withParsedBody(response));
  }
}

export default new ChatAPI();
